import { Icon } from '@iconify/react';
import Checkbox from '@mui/material/Checkbox';
import { DateTime } from 'luxon';
import Moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import Swal from 'sweetalert2';
import {
  DataTable,
  IOptions,
  StyledButton,
  StyledDateInput,
  StyledSelect,
} from '../../../../components';
import { Loading } from '../../../../components/Loading';
import { useFilters } from '../../../../services/contexts/FiltersContext';
import { useUserDocument } from '../../../../services/contexts/UserDocumentContext';
import {
  GetAnticipationResult,
  useAntecipacao,
} from '../../../../services/hooks/useAntecipacao';
import { useEstabelecimento } from '../../../../services/hooks/useEstabelecimento';
import { AddLocaleDateString } from '../../../../utils/AddLocaleDateString';
import { FormatValue } from '../../../../utils/FormatValue';
import { NewFormatValue } from '../../../../utils/NewFormatValue';
import { SumDaysFromNow } from '../../../../utils/SumDaysFromNow';
import { UnMaskFormat } from '../../../../utils/UnMaskFormat';
import { YearDaysFromNow } from '../../../../utils/YearDaysFromNow';
import { AdminTitle } from '../../components';
import { AnticipationModal } from '../../components/AnticipationModal';
import { AnticipationValueModal } from '../../components/AnticipationValueModal';
import { InputDescription } from '../../components/InputDescription';
import { NoAnticipationModal } from '../../components/NoAnticipationModal';
import {
  ContaContainer,
  FiltersContainer,
  IconContainer,
  Row,
  Td,
} from './styles';

import ReactSelectMulti from '../../../../components/ReactSelectMulti';
import { checkLengthToMaskCpfOrCnpj } from '../../../../utils/checkLengthToMaskCpfOrCnpj';
import { listEstablishmentsOptionsToSet } from '../../../../utils/listEstablishmentsOptionsToSet';

export interface AggregatedValue {
  vlBruto: number;
  vlTxAdm: number;
  vlTxAntSimulado: number;
  vlPos: number;
  vlLiquido: number;
  estabDocto?: string;
  listItemsIdOrdemRepasse?: { idOrdemRepasse: string }[];
  estabFantasia?: string;
}

export const initialValues: AggregatedValue = {
  vlBruto: 0,
  vlTxAdm: 0,
  vlTxAntSimulado: 0,

  vlPos: 0,
  vlLiquido: 0,
  estabDocto: '',
  listItemsIdOrdemRepasse: [],
  estabFantasia: '',
};

export const Antecipacao = () => {
  const navigate = useNavigate();
  const { documentSelected, emailUserSelected } = useUserDocument();
  const { fetch, selectAnticipationValue } = useAntecipacao();

  const today = useMemo(() => new Date(), []);
  const anticipationDateLimit = new Date(today);
  anticipationDateLimit.setMonth(today.getMonth() + 3);

  const docto = useMemo(() => documentSelected as string, [documentSelected]);
  const [inputs, setInputs] = useState<AggregatedValue>({ ...initialValues });

  const [currentTime, setCurrentTime] = useState(new Date());

  const now = DateTime.now()
    .setLocale('pt-br')
    .toLocaleString(DateTime.DATE_HUGE);

  const {
    initialDate,
    setInitialDate,
    finalDate,
    setFinalDate,
    productType,
    setProductType,
    desiredValue,
    setDesiredValue,
    anticipationDate,
    setAnticipationDate,
  } = useFilters();

  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [checked, setChecked] = useState<GetAnticipationResult[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [anticipationModalIsOpen, setAnticipationModalIsOpen] = useState(false);
  const [isValueModalOpen, setIsValueModalOpen] = useState(false);
  const [disabledAllCheck, setDisabledAllCheck] = useState(false);
  const [desiredList, setDesiredList] = useState<
    GetAnticipationResult[] | undefined
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(false);

  const [desiredListBackup, setDesiredListBackup] = useState<
    GetAnticipationResult[] | undefined
  >();
  const [
    listEstablishmentsAvailableToFilter,
    setListEstablishmentsAvailableToFilter,
  ] = useState<IOptions[]>([] as IOptions[]);

  const [desiredListHasSelected, setDesiredListHasSelected] = useState<any[]>(
    []
  );

  function handleOpenModal() {
    setAnticipationModalIsOpen(true);
  }

  function handleCloseModal() {
    setAnticipationModalIsOpen(false);
  }

  useEffect(() => {
    setChecked([]);
    setDesiredList([]);
    setIsLoaded(false);
    setError(false);
  }, [initialDate, finalDate, productType, anticipationDate]);

  const handleFilterList = useCallback(async () => {
    const newParams = {
      docto: docto,
      dtInicial: AddLocaleDateString(initialDate),
      dtFinal: AddLocaleDateString(finalDate),
      dtAntecipacao: AddLocaleDateString(anticipationDate),
      tipoProd: productType,
      pagina: page,
      limite: itemsPerPage,
    };

    try {
      setIsLoading(true);
      const data = await fetch(newParams);
      setDesiredList(data);
      setDesiredListBackup(data);

      const optionsToSet = listEstablishmentsOptionsToSet(data);
      setListEstablishmentsAvailableToFilter(optionsToSet);

      setError(false);
    } catch (e) {
      console.log(e);
      setError(true);
    } finally {
      setIsLoaded(true);
      setIsLoading(false);
    }
  }, [
    docto,
    initialDate,
    finalDate,
    anticipationDate,
    productType,
    page,
    itemsPerPage,
    fetch,
  ]);

  const {
    data: estabelecimento,
    isFetched: isEstabelecimentoFetched,
    isLoading: isLoadingEstabelecimento,
  } = useEstabelecimento({ docto: docto ?? '' });

  function handleSetDesiredValue(e: any) {
    setDesiredValue(FormatValue(e.target.value));
  }

  const handleDesiredValue = async () => {
    const newDesiredValue = UnMaskFormat(desiredValue);
    if (newDesiredValue === '' || newDesiredValue === 0) {
      setChecked([]);
      return;
    }

    const newParams = {
      docto: docto,
      dtInicial: AddLocaleDateString(initialDate),
      dtFinal: AddLocaleDateString(finalDate),
      dtAntecipacao: AddLocaleDateString(anticipationDate),
      tipoProd: productType,
      pagina: page,
      limite: itemsPerPage,
      value: parseFloat(newDesiredValue),
    };
    try {
      setIsLoading(true);

      const anticipationSelected = await selectAnticipationValue(
        newParams,
        desiredListHasSelected?.map((v) => v?.value)
      );
      setChecked(anticipationSelected);
      setIsValueModalOpen(false);
    } catch (e) {
      console.log(e);
      Swal.fire({
        title: 'Erro',
        html: 'Não foi possível aplicar o valor de forma automática. Por favor, aplique manualmente selecionando cada transação desejada.',
        showConfirmButton: true,

        confirmButtonColor: '#F58220',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetSelectAll = () => {
    const list = desiredList;
    const checkedIdsSet = new Set(checked.map((c) => c.idOrdemRepasse));

    setSelectAll(!selectAll);

    if (!selectAll === true) {
      const newListChecked = [...checked];
      list?.forEach((newValue) => {
        if (
          !checkedIdsSet.has(newValue.idOrdemRepasse) &&
          newValue.permiteAntecipacao === 'S'
        ) {
          newListChecked.push(newValue);
        }
      });

      setChecked(newListChecked);
    } else {
      const toRemove = new Set();
      desiredList?.forEach((d) => {
        if (checkedIdsSet.has(d.idOrdemRepasse)) {
          toRemove.add(d.idOrdemRepasse);
        }
      });
      const newChecked = checked.filter((c) => !toRemove.has(c.idOrdemRepasse));
      setChecked(newChecked);
    }
  };

  function isChecked(anticipation: GetAnticipationResult) {
    return !!checked.find(
      (a) => a.idOrdemRepasse === anticipation.idOrdemRepasse
    );
  }

  function handleCheck(anticipation: GetAnticipationResult) {
    let listChecked: GetAnticipationResult[] = [...checked];

    if (
      !listChecked.find((a) => a.idOrdemRepasse === anticipation.idOrdemRepasse)
    ) {
      listChecked = [...listChecked, anticipation];
    } else {
      listChecked = listChecked.filter(
        (a) => a.idOrdemRepasse !== anticipation.idOrdemRepasse
      );
    }

    setChecked(listChecked);
    setSelectAll(false);
  }

  const tableHead = [
    <div>
      <Checkbox
        size="small"
        name="selectAll"
        onChange={handleSetSelectAll}
        checked={selectAll}
        sx={{
          color: '#4D4C4D !important',
          '&.Mui-checked': {
            color: '#F58220 !important',
          },
        }}
      />
    </div>,
    'DOC',
    'Nº Guia',
    'Nome Fantasia',
    'Data Vencimento',
    'Prazo',
    'Valor Parcela',
    'Taxa Adm',
    'Taxa Antecipação',
    'Aluguel POS',
    'Outros Descontos',
    'Valor Líquido',
  ];

  useEffect(() => {
    if (itemsPerPage) {
      setPage(1);
      setIsLoaded(false);
    }
  }, [docto]);

  useEffect(() => {
    if (itemsPerPage && isLoaded) {
      if (page === 1) handleFilterList();
      setPage(1);
    }
  }, [itemsPerPage]);

  useEffect(() => {
    if (isLoaded) {
      handleFilterList();
      setSelectAll(false);
    }
  }, [page, fetch]);

  useEffect(() => {
    handleCalculateCheckedValues();
  }, [checked]);

  useEffect(() => {
    if (desiredList) {
      selectAllCheckboxIfAllAreSelected();
    }
  }, [desiredList, checked]);

  const selectAllCheckboxIfAllAreSelected = () => {
    const checkedIds = new Set<string>(checked.map((c) => c.idOrdemRepasse));
    const areAllSelected = (desiredList ?? []).every(
      (c) => c.permiteAntecipacao === 'N' || checkedIds.has(c.idOrdemRepasse)
    );
    if (areAllSelected && checkedIds.size > 0) {
      setSelectAll(true);
    }
  };

  function handleAnticipationCheckedValue() {
    let vlOpen = 0;

    if (checked.length > 0) {
      checked?.map(
        (check: any) => (vlOpen += check.vlAberto - check.vlTxAntSimulado)
      );
    }

    return vlOpen;
  }

  function handleCalculateCheckedValues() {
    const aggregateValue = checked.reduce<AggregatedValue>(
      (acc: AggregatedValue, obj: any) => {
        return {
          estabDocto: obj?.estabDocto,
          vlBruto: (acc.vlBruto += obj.vlParcela),
          vlTxAdm: (acc.vlTxAdm += obj.vlTxAdm),
          vlTxAntSimulado: (acc.vlTxAntSimulado += obj.vlTxAntSimulado),

          vlPos: (acc.vlPos += obj.vlLocacao),
          vlLiquido: (acc.vlLiquido += obj.vlAberto - obj.vlTxAntSimulado),
        };
      },
      { ...initialValues }
    );

    setInputs(aggregateValue);
  }

  useEffect(() => {
    setInitialDate(SumDaysFromNow(today, 4));
    setFinalDate(SumDaysFromNow(today, 30));
    var timer = setInterval(() => setCurrentTime(new Date()), 10000);

    return function cleanup() {
      clearInterval(timer);
    };
  }, []);

  const handleToSetListDesiredList = (value: any[]) => {
    setChecked([]);
    setSelectAll(false);

    if (!value?.length) {
      setDesiredList(desiredListBackup);
      setDesiredListHasSelected([]);
      setError(false);
    } else {
      const listFiltered = desiredListBackup?.filter((item) =>
        value.some((v) => v?.value === item?.estabDocto)
      );

      setDesiredListHasSelected(value);
      setDesiredList(listFiltered);
      setError(false);
    }
  };

  return (
    <ContaContainer>
      <p className="date">{now}</p>
      <AdminTitle
        title="Conta Pagamento > Antecipação"
        icon={<Icon icon="tabler:pig-money" />}
      />

      <NoAnticipationModal
        isOpen={
          isEstabelecimentoFetched &&
          (estabelecimento?.permiteAntecipacao !== 'S' ||
            estabelecimento?.servicoPedidoAntecipacao?.status !== 1)
        }
        onRequestClose={() => {
          navigate('/admin/dashboard');
          window.location.reload();
        }}
        servicoPedidoAntecipacao={estabelecimento?.servicoPedidoAntecipacao}
      />

      <AnticipationModal
        isOpen={anticipationModalIsOpen}
        onRequestClose={handleCloseModal}
        anticipationList={checked}
        checkedValues={inputs}
        dtAnticipation={AddLocaleDateString(anticipationDate)}
        user={{
          docto: docto,
          email: emailUserSelected,
        }}
      />
      <AnticipationValueModal
        isOpen={isValueModalOpen}
        onRequestClose={() => {
          setIsValueModalOpen(false);
        }}
        handleDesiredValue={handleDesiredValue}
        handleSetDesiredValue={handleSetDesiredValue}
        desiredValue={desiredValue}
      />

      <FiltersContainer>
        <div>
          <InputDescription
            placeholder="Data inicial"
            showTooltip={true}
            tooltipText="Importante: Este campo possui um limite de 90 dias. Caso surjam dúvidas, sinta-se à vontade para entrar em contato conosco."
          />
          <StyledDateInput
            value={initialDate}
            onChange={setInitialDate}
            placeholder="Data inicial"
            minDate={SumDaysFromNow(today, 4)}
            maxDate={today ? YearDaysFromNow(today) : today}
          />
        </div>

        <div>
          <InputDescription placeholder="Data final" />
          <StyledDateInput
            value={finalDate}
            onChange={setFinalDate}
            placeholder="Data final"
            minDate={SumDaysFromNow(today, 4)}
            maxDate={today ? YearDaysFromNow(today) : today}
          />
        </div>

        <div>
          <InputDescription placeholder="Tipo de Produto" />
          <StyledSelect
            placeholder="Tipo de Produto"
            options={[
              { id: 'DB', value: 'Débito' },
              { id: 'CV', value: 'Crédito à Vista' },
              { id: 'CP', value: 'Crédito Parcelado' },
              { id: 'PX', value: 'Pix' },
            ]}
            setValue={setProductType}
            value={productType}
          />
        </div>

        <div>
          <InputDescription placeholder="Data de antecipação" />
          <StyledDateInput
            value={anticipationDate}
            onChange={setAnticipationDate}
            minDate={SumDaysFromNow(today, 1)}
            maxDate={anticipationDateLimit}
            placeholder="Inserir a data desejada"
            disableWeekends={true}
            calendarViewMode="month"
            calendarMinDetail="month"
            readOnlyInput={true}
          />
        </div>

        <StyledButton
          icon={<Icon icon="clarity:filter-solid" width={18} />}
          iconPosition="right"
          text="Filtrar"
          onClick={handleFilterList}
          disabled={
            isLoaded || (initialDate && finalDate && anticipationDate)
              ? false
              : true
          }
        />
      </FiltersContainer>
      {isLoading || isLoadingEstabelecimento ? (
        <Loading />
      ) : (
        isLoaded && (
          <>
            <FiltersContainer>
              <div className="selectInput">
                <InputDescription placeholder="Estabelecimentos" />
                <ReactSelectMulti
                  placeholder="Filtrar por Estabelecimento"
                  setSelectedOptions={handleToSetListDesiredList}
                  allOptions={listEstablishmentsAvailableToFilter.map(
                    (each) => ({ value: each.id, label: each.value })
                  )}
                  values={desiredListHasSelected}
                />
              </div>

              <StyledButton
                text="Definir valor de antecipação"
                iconPosition="right"
                icon={
                  <IconContainer>
                    <Icon
                      icon="iconamoon:arrow-right-6-circle-fill"
                      width={20}
                    />
                  </IconContainer>
                }
                onClick={() => {
                  setIsValueModalOpen(true);
                }}
                color="#3597E9"
                style={{ padding: '0px 20px' }}
              />
              <StyledButton
                text="Simular Antecipação"
                icon={
                  <IconContainer>
                    <Icon
                      icon="iconamoon:arrow-right-6-circle-fill"
                      width={20}
                    />
                  </IconContainer>
                }
                iconPosition="right"
                style={{ padding: '0px 20px' }}
                onClick={() => {
                  const today18 = new Date();
                  today18.setHours(18);
                  today18.setMinutes(0);
                  today18.setSeconds(0);
                  if (currentTime > today18) {
                    Swal.fire({
                      title: 'Erro',
                      html: 'Infelizmente, não foi possível realizar a simulação de antecipação por conta do horário. Por favor, tente novamente mais tarde.',
                      showConfirmButton: true,

                      confirmButtonColor: '#F58220',
                    });
                  } else {
                    handleOpenModal();
                  }
                }}
                color="#3597E9"
                disabled={inputs.vlBruto === 0}
              />
            </FiltersContainer>

            <DataTable
              title="Antecipação de Recebíveis"
              heads={tableHead}
              hasPagination
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              page={page}
              setPage={setPage}
              totalItems={desiredList?.length}
              showCardValue
              titleValue="Valor selecionado"
              valueCardValue={handleAnticipationCheckedValue()}
              itemsPerPageOptions={[100]}
            >
              <tbody>
                {error ? (
                  <Row>
                    <Td colSpan={11}>Falha ao obter dados</Td>
                  </Row>
                ) : desiredList?.length === 0 ? (
                  <Row>
                    <Td colSpan={11}>Não foram encontrados dados.</Td>
                  </Row>
                ) : (
                  desiredList &&
                  desiredList.map((newData: any) => (
                    <Row
                      disabled={newData?.permiteAntecipacao}
                      key={newData.idOrdemRepasse}
                    >
                      <Td>
                        <div className="checkbox-container">
                          <Checkbox
                            size="small"
                            readOnly
                            onChange={() => {
                              handleCheck(newData);
                            }}
                            disabled={newData?.permiteAntecipacao === 'N'}
                            // checked={isChecked(newData) || selectAll}
                            checked={isChecked(newData)}
                            sx={{
                              color: '#4D4C4D !important',
                              '&.Mui-checked': {
                                color: '#F58220 !important',
                              },
                            }}
                          />
                        </div>
                      </Td>
                      <Td>
                        {!!newData?.estabDocto &&
                          checkLengthToMaskCpfOrCnpj(newData?.estabDocto)}
                      </Td>
                      <Td>{newData?.numOrdem}</Td>
                      <Td>{newData?.estabFantasia}</Td>
                      <Td>
                        {newData?.dtPrevPgto
                          ? Moment(newData.dtPrevPgto).format('DD/MM/yyyy')
                          : ''}
                      </Td>
                      <Td>{`${newData?.parcela} ${
                        newData?.parcela > 1 ? 'meses' : 'mês'
                      }`}</Td>
                      <Td>{NewFormatValue(newData?.vlParcela)}</Td>
                      <Td>{NewFormatValue(newData?.vlTxAdm)}</Td>
                      <Td>{NewFormatValue(newData?.vlTxAntSimulado)}</Td>
                      <Td>{NewFormatValue(newData?.vlLocacao)}</Td>
                      <Td>{NewFormatValue(newData?.vlOutrosDesc)}</Td>
                      <Td>
                        {NewFormatValue(
                          newData?.vlAberto - newData?.vlTxAntSimulado
                        )}
                      </Td>
                    </Row>
                  ))
                )}
              </tbody>
            </DataTable>
          </>
        )
      )}
    </ContaContainer>
  );
};
